import React, { useEffect, useRef, useState } from 'react';
import { keyframes } from '@emotion/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';

import {
  analyzeWeeklyReadingPreviewAudio,
  completeWeeklyReadingAttempt,
  cancelWeeklyReadingAttempt,
  fetchWeeklyReadingAnalysis,
  startWeeklyReadingAttempt,
  uploadWeeklyReadingAudio,
} from '../miniTasksApi';
import { nextAudioAnalysisPollDelay } from './audioAnalysisPolling';
import MicrophonePermissionHelp from '../../components/microphone/MicrophonePermissionHelp';

const CONTINUE_PROMPT_AFTER_MS = 3 * 60 * 1000;
const CONTINUE_PROMPT_REPEAT_MS = 2 * 60 * 1000;
const CONTINUE_PROMPT_RESPONSE_MS = 20 * 1000;
const MAX_READING_DURATION_MS = 10 * 60 * 1000;
const ANALYSIS_MESSAGES = [
  { afterMs: 0, text: '✨ Okumanı inceliyoruz…' },
  { afterMs: 4000, text: 'Sesini ve okuduğun metni karşılaştırıyoruz. 🔍' },
  { afterMs: 10000, text: 'Okumanla ilgili sonuçlarını hazırlıyoruz. 🌟' },
  { afterMs: 18000, text: 'Biraz sabır, az sonra sonuçlarını göreceksin. 😊' },
  { afterMs: 30000, text: 'Bazen bu işlem biraz daha uzun sürebilir.\nMerak etme, hâlâ çalışıyoruz. ✨' },
  { afterMs: 50000, text: 'Neredeyse hazır… Biraz daha bizimle kal. 🌈' },
];

const messageEntrance = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

function AnalysisWaitingScreen() {
  const [visibleCount, setVisibleCount] = useState(1);

  useEffect(() => {
    const timers = ANALYSIS_MESSAGES.slice(1).map(({ afterMs }, index) => (
      window.setTimeout(() => setVisibleCount(index + 2), afterMs)
    ));
    return () => timers.forEach(window.clearTimeout);
  }, []);

  return (
    <Box
      role="status"
      aria-label="Okuma analizi hazırlanıyor"
      sx={{
        minHeight: '100vh',
        px: 2,
        py: 5,
        display: 'grid',
        placeItems: 'center',
        bgcolor: '#fffaf0',
        backgroundImage: 'radial-gradient(circle at 8% 10%, rgba(56,189,248,0.13), transparent 28%), radial-gradient(circle at 92% 90%, rgba(132,204,22,0.13), transparent 28%)',
      }}
    >
      <Stack spacing={3} alignItems="center" sx={{ width: '100%', maxWidth: 620, textAlign: 'center' }}>
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            background: 'linear-gradient(145deg, #ede9fe, #dbeafe)',
            boxShadow: '0 16px 36px rgba(79,70,229,0.12)',
          }}
        >
          <CircularProgress size={48} thickness={4} aria-hidden="true" sx={{ color: '#7c3aed' }} />
        </Box>
        <Stack spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
          {ANALYSIS_MESSAGES.slice(0, visibleCount).map(({ text, afterMs }, index) => (
            <Typography
              key={afterMs}
              sx={{
                color: index === 0 ? '#312e81' : '#475569',
                fontWeight: index === 0 ? 950 : 750,
                fontSize: index === 0 ? { xs: 25, sm: 31 } : { xs: 16, sm: 18 },
                lineHeight: 1.5,
                whiteSpace: 'pre-line',
                animation: `${messageEntrance} 400ms ease-out both`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            >
              {text}
            </Typography>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}


function createIdempotencyKey(prefix) {
  const randomPart = window.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${randomPart}`.slice(0, 64);
}

function formatDuration(elapsedMs) {
  const totalSeconds = Math.max(1, Math.round(Number(elapsedMs || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes ? `${minutes} dk ${seconds} sn` : `${seconds} sn`;
}

function currentScrollRatio() {
  const root = document.documentElement;
  const totalHeight = Math.max(root?.scrollHeight || 0, document.body?.scrollHeight || 0);
  if (!totalHeight || totalHeight <= window.innerHeight) return 1;
  return Math.min(1, Math.max(0, (window.scrollY + window.innerHeight) / totalHeight));
}

function formatPlayerTime(seconds) {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  return `${Math.floor(safeSeconds / 60)}:${String(safeSeconds % 60).padStart(2, '0')}`;
}

function accuracyMessage(value) {
  const score = Number(value);
  if (score >= 95) return 'Muhteşem okuma! ⭐';
  if (score >= 85) return 'Çok iyi gidiyorsun! 🌟';
  if (score >= 70) return 'Güzel bir çalışma! ✨';
  return 'Her okumada daha da güçlenirsin! 🌱';
}

function ResultMetricCard({ icon, label, color, background, children, helper }) {
  return (
    <Box
      sx={{
        minWidth: 0,
        p: { xs: 2, sm: 2.4 },
        borderRadius: 3.5,
        border: '1px solid rgba(148,163,184,0.18)',
        background,
        boxShadow: '0 12px 30px rgba(30,41,59,0.06)',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color, mb: 1.2 }}>
        {icon}
        <Typography sx={{ fontWeight: 900 }}>{label}</Typography>
      </Stack>
      {children}
      {helper ? (
        <Typography sx={{ mt: 1.2, color, fontWeight: 800, fontSize: 14 }}>{helper}</Typography>
      ) : null}
    </Box>
  );
}

const ALIGNMENT_STYLES = {
  match: { label: 'Eşleşen', color: '#047857', background: '#ecfdf5', border: '#a7f3d0' },
  substitution: { label: 'Olası farklı okuma', color: '#6d28d9', background: '#f5f3ff', border: '#c4b5fd' },
  omission: { label: 'Olası atlama', color: '#c2410c', background: '#fff7ed', border: '#fdba74' },
  insertion: { label: 'Olası ekleme', color: '#0369a1', background: '#f0f9ff', border: '#7dd3fc' },
  repetition: { label: 'Olası tekrar', color: '#be123c', background: '#fff1f2', border: '#fda4af' },
  uncertain: { label: 'Kontrol edilebilir', color: '#a16207', background: '#fefce8', border: '#fde047' },
};

function ReadingAlignment({ rows }) {
  if (!Array.isArray(rows) || !rows.length) return null;

  return (
    <Paper
      variant="outlined"
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3.5, borderColor: '#c7d2fe', bgcolor: '#fafaff' }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <DescriptionRoundedIcon sx={{ color: '#4f46e5' }} />
        <Box>
          <Typography sx={{ fontWeight: 950, fontSize: 19 }}>Okuma karşılaştırman</Typography>
          <Typography sx={{ color: '#64748b', fontSize: 14 }}>
            Bu sonuç tahminidir. İşaretlenen kelimeleri okuma kaydınla birlikte kontrol edebilirsin.
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.8} sx={{ my: 1.8 }}>
        {Object.entries(ALIGNMENT_STYLES).map(([operation, style]) => (
          <Chip
            key={operation}
            size="small"
            label={style.label}
            sx={{ color: style.color, bgcolor: style.background, border: `1px solid ${style.border}`, fontWeight: 800 }}
          />
        ))}
      </Stack>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 0.8,
          p: { xs: 1.4, sm: 2 },
          borderRadius: 2.5,
          bgcolor: '#fff',
          lineHeight: 1.5,
        }}
      >
        {rows.map((row, index) => {
          const operation = ALIGNMENT_STYLES[row?.operation] ? row.operation : 'uncertain';
          const style = ALIGNMENT_STYLES[operation];
          const reference = row?.reference;
          const transcript = row?.transcript;
          const isMatch = operation === 'match';
          const transcriptOnly = !reference && transcript;
          return (
            <Box
              component="span"
              key={`${index}-${operation}-${reference || transcript || ''}`}
              aria-label={`${style.label}: ${reference || transcript || ''}${reference && transcript && !isMatch ? `, algılanan ${transcript}` : ''}`}
              sx={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: isMatch ? 36 : 52,
                px: isMatch ? 0.8 : 1,
                py: 0.45,
                borderRadius: 1.6,
                color: style.color,
                bgcolor: style.background,
                border: `1px ${operation === 'omission' ? 'dashed' : 'solid'} ${style.border}`,
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: 'inherit',
                  fontWeight: isMatch ? 750 : 900,
                  fontSize: 16,
                  lineHeight: 1.2,
                }}
              >
                {reference || transcript}
              </Typography>
              {!isMatch ? (
                <Typography component="span" sx={{ color: 'inherit', fontWeight: 850, fontSize: 10, lineHeight: 1.3 }}>
                  {reference && transcript ? `→ ${transcript}` : transcriptOnly ? `+ ${style.label}` : style.label}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

function TransientAudioPlayer({ src, fallbackDurationMs }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);

  const fallbackDuration = Math.max(1, Number(fallbackDurationMs || 0) / 1000);
  const duration = Number.isFinite(mediaDuration) && mediaDuration > 0
    ? mediaDuration
    : fallbackDuration;

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setMediaDuration(0);
  }, [src]);

  const readMediaDuration = (audio) => {
    const reportedDuration = Number(audio?.duration);
    if (Number.isFinite(reportedDuration) && reportedDuration > 0) {
      setMediaDuration(reportedDuration);
    }
  };

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      await audio.play();
    } else {
      audio.pause();
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        p: { xs: 2, sm: 2.4 },
        borderRadius: 3.5,
        borderColor: '#bfdbfe',
        background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)',
      }}
    >
      <audio
        aria-label="Kendi okuma kaydın"
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(event) => {
          const nextTime = Number(event.currentTarget.currentTime);
          setCurrentTime(Number.isFinite(nextTime) ? Math.max(0, nextTime) : 0);
          readMediaDuration(event.currentTarget);
        }}
        onLoadedMetadata={(event) => readMediaDuration(event.currentTarget)}
        onDurationChange={(event) => readMediaDuration(event.currentTarget)}
      >
        <track kind="captions" />
      </audio>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: { sm: 260 } }}>
          <IconButton
            aria-label={playing ? 'Okuma kaydını duraklat' : 'Okuma kaydını oynat'}
            onClick={togglePlayback}
            sx={{
              width: 58,
              height: 58,
              color: '#fff',
              background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
              boxShadow: '0 10px 22px rgba(79,70,229,0.28)',
              '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #4338ca)' },
            }}
          >
            {playing ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
          </IconButton>
          <Box>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <GraphicEqRoundedIcon sx={{ color: '#4f46e5' }} />
              <Typography sx={{ fontWeight: 950, fontSize: 18 }}>Sesini dinle</Typography>
            </Stack>
            <Typography sx={{ color: '#64748b', fontSize: 14 }}>Bu sayfadan çıkınca kayıt silinir.</Typography>
          </Box>
        </Stack>
        <Box sx={{ flex: 1 }}>
          <Slider
            aria-label="Ses kaydı konumu"
            min={0}
            max={duration || 1}
            step={0.1}
            value={Math.min(currentTime, duration || 1)}
            onChange={(_event, value) => {
              const nextTime = Math.min(duration, Math.max(0, Number(value) || 0));
              try {
                if (audioRef.current) audioRef.current.currentTime = nextTime;
                setCurrentTime(nextTime);
              } catch (_) {
                // Bazı MediaRecorder WebM blob'ları seek bilgisini geç üretir; oynatma etkilenmez.
              }
            }}
            sx={{ color: '#6d28d9', py: 0.5 }}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800 }}>
              {formatPlayerTime(currentTime)} / {formatPlayerTime(duration)}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: '#2563eb' }}>
              <VolumeUpRoundedIcon fontSize="small" />
              <Typography variant="caption" sx={{ fontWeight: 800 }}>Geçici kayıt</Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function WeeklyReadingRoundView({
  round,
  boardSessionId,
  previewToken,
  classTitle,
  onExit,
  onSkip,
  onCompleted,
}) {
  const passage = round?.passage || {};
  const isPreview = Boolean(round?.preview);
  const resumedAttempt = round?.active_attempt || null;
  const [phase, setPhase] = useState('preparation');
  const [attempt, setAttempt] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [skipping, setSkipping] = useState(false);
  const [audioPlaybackUrl, setAudioPlaybackUrl] = useState('');
  const [continuationPromptDeadline, setContinuationPromptDeadline] = useState(null);
  const [continuationSeconds, setContinuationSeconds] = useState(20);
  const startKeyRef = useRef(createIdempotencyKey('reading-start'));
  const completeKeyRef = useRef(createIdempotencyKey('reading-complete'));
  const startedAtMsRef = useRef(null);
  const visibilityHiddenAtRef = useRef(null);
  const hiddenCountRef = useRef(0);
  const hiddenMsRef = useRef(0);
  const maxScrollRatioRef = useRef(currentScrollRatio());
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioPlaybackUrlRef = useRef('');
  const nextContinuationPromptAtRef = useRef(null);
  const measurementActiveRef = useRef(false);
  const cancellationInProgressRef = useRef(false);
  const cancelActiveReadingRef = useRef(null);
  const cancellationContextRef = useRef(null);
  const mountedRef = useRef(true);
  const startInProgressRef = useRef(false);
  const startupGenerationRef = useRef(0);

  const notifyCancellation = (reason) => {
    const context = cancellationContextRef.current;
    cancellationContextRef.current = null;
    if (context) {
      // Unload delivery is best effort; server expiry is the authoritative fallback.
      cancelWeeklyReadingAttempt(context.assignmentId, context.attemptId, context.sessionId, reason)
        .catch(() => {});
    }
  };

  const visiblePassage = attempt?.passage || passage;

  const releaseMicrophone = () => {
    mediaStreamRef.current?.getTracks?.().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
  };

  const releasePlaybackAudio = () => {
    if (audioPlaybackUrlRef.current && window.URL?.revokeObjectURL) {
      window.URL.revokeObjectURL(audioPlaybackUrlRef.current);
    }
    audioPlaybackUrlRef.current = '';
    setAudioPlaybackUrl('');
  };

  useEffect(() => {
    mountedRef.current = true;
    const handlePageHide = () => {
      startupGenerationRef.current += 1;
      // A permission prompt or start request may still be pending when the page closes.
      if (startInProgressRef.current) releaseMicrophone();
      cancelActiveReadingRef.current?.('Okuma kaydı kapatıldı. Yeniden başlayabilirsin.', 'page_closed');
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      mountedRef.current = false;
      startupGenerationRef.current += 1;
      window.removeEventListener('pagehide', handlePageHide);
      if (measurementActiveRef.current) notifyCancellation('exit');
      measurementActiveRef.current = false;
      releaseMicrophone();
      if (audioPlaybackUrlRef.current && window.URL?.revokeObjectURL) {
        window.URL.revokeObjectURL(audioPlaybackUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const status = result?.analysis?.status;
    if (isPreview || !attempt?.attempt_id || !['uploaded', 'processing'].includes(status)) return undefined;
    let cancelled = false;
    let timer = null;
    let pendingChecks = 0;

    const scheduleNext = () => {
      if (!cancelled) timer = window.setTimeout(
        pollAnalysis, nextAudioAnalysisPollDelay(++pendingChecks),
      );
    };
    const pollAnalysis = async () => {
      try {
        const analysis = await fetchWeeklyReadingAnalysis(
          round.student_assignment_id,
          attempt.attempt_id,
          boardSessionId,
        );
        if (cancelled) return;
        setResult((current) => ({ ...current, analysis }));
        if (['uploaded', 'processing'].includes(analysis?.status)) {
          scheduleNext();
        }
      } catch (_) {
        // WPM result remains persistent. Avoid pounding RDS on temporary errors.
        scheduleNext();
      }
    };

    timer = window.setTimeout(pollAnalysis, nextAudioAnalysisPollDelay(0));
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [attempt?.attempt_id, boardSessionId, isPreview, result?.analysis?.status, round.student_assignment_id]);

  const startAudioRecording = async (generation) => {
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      throw new Error('Bu tarayıcı ses kaydını desteklemiyor. Güncel Chrome veya Safari ile tekrar deneyin.');
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (['NotAllowedError', 'PermissionDeniedError', 'SecurityError'].includes(err?.name)) {
        const permissionError = new Error('Mikrofonu açmamız gerekiyor');
        permissionError.name = 'MicrophonePermissionError';
        throw permissionError;
      }
      if (['NotFoundError', 'DevicesNotFoundError'].includes(err?.name)) {
        throw new Error('Mikrofon bulunamadı. Cihazına bir mikrofon bağlı olduğundan emin ol ve tekrar başla.');
      }
      if (['NotReadableError', 'TrackStartError'].includes(err?.name)) {
        throw new Error('Mikrofona erişilemiyor. Mikrofonu kullanan diğer uygulamaları kapatıp tekrar başla.');
      }
      throw new Error('Ses kaydı başlatılamadı. Mikrofon bağlantısını ve tarayıcının mikrofon izinlerini kontrol edip tekrar başla.');
    }
    if (!mountedRef.current || generation !== startupGenerationRef.current) {
      stream.getTracks().forEach(track => track.stop());
      return;
    }
    // Own the stream before constructing/starting the recorder so either failure releases it.
    mediaStreamRef.current = stream;
    const preferredType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
      .find((type) => window.MediaRecorder.isTypeSupported?.(type));
    const recorder = preferredType
      ? new window.MediaRecorder(stream, { mimeType: preferredType })
      : new window.MediaRecorder(stream);
    audioChunksRef.current = [];
    recorder.ondataavailable = (event) => {
      if (event.data?.size) audioChunksRef.current.push(event.data);
    };
    recorder.start(1000);
    mediaRecorderRef.current = recorder;
  };

  const stopAudioRecording = () => new Promise((resolve) => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      releaseMicrophone();
      resolve(null);
      return;
    }
    recorder.onstop = () => {
      const mimeType = String(recorder.mimeType || 'audio/webm').split(';', 1)[0];
      const blob = new Blob(audioChunksRef.current, { type: mimeType });
      releaseMicrophone();
      resolve(blob);
    };
    recorder.stop();
  });

  const cancelActiveReading = async (message, reason = 'exit') => {
    if (!measurementActiveRef.current || cancellationInProgressRef.current) return;
    measurementActiveRef.current = false;
    cancellationInProgressRef.current = true;
    setPhase('cancelling');
    setContinuationPromptDeadline(null);
    notifyCancellation(reason);
    try {
      await stopAudioRecording();
    } finally {
      audioChunksRef.current = [];
      if (!mountedRef.current) return;
      setAttempt(null);
      setResult(null);
      setError('');
      setNotice(message);
      startKeyRef.current = createIdempotencyKey('reading-start');
      completeKeyRef.current = createIdempotencyKey('reading-complete');
      startedAtMsRef.current = null;
      hiddenCountRef.current = 0;
      hiddenMsRef.current = 0;
      visibilityHiddenAtRef.current = null;
      maxScrollRatioRef.current = currentScrollRatio();
      nextContinuationPromptAtRef.current = null;
      cancellationInProgressRef.current = false;
      setPhase('preparation');
    }
  };
  cancelActiveReadingRef.current = cancelActiveReading;

  useEffect(() => {
    if (phase !== 'reading') return undefined;

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (visibilityHiddenAtRef.current == null) {
          hiddenCountRef.current += 1;
          visibilityHiddenAtRef.current = Date.now();
        }
      } else if (visibilityHiddenAtRef.current != null) {
        const hiddenForMs = Math.max(0, Date.now() - visibilityHiddenAtRef.current);
        hiddenMsRef.current += hiddenForMs;
        visibilityHiddenAtRef.current = null;
      }
    };
    const handleScroll = () => {
      maxScrollRatioRef.current = Math.max(maxScrollRatioRef.current, currentScrollRatio());
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'reading') return undefined;

    const checkReadingLifetime = () => {
      if (!measurementActiveRef.current) return;
      const now = Date.now();
      if (
        startedAtMsRef.current != null
        && now - startedAtMsRef.current >= MAX_READING_DURATION_MS
      ) {
        cancelActiveReadingRef.current?.(
          'Okuma kaydı çok uzun süre açık kaldığı için durduruldu. Ses yüklenmedi; yeniden başlayabilirsin.',
          'timeout',
        );
        return;
      }
      if (continuationPromptDeadline != null) {
        const secondsLeft = Math.max(0, Math.ceil((continuationPromptDeadline - now) / 1000));
        setContinuationSeconds(secondsLeft);
        if (secondsLeft <= 0) {
          cancelActiveReadingRef.current?.(
            'Devam onayı alınamadığı için bu ölçümü durdurduk. Ses yüklenmedi; yeniden başlayabilirsin.',
            'no_confirmation',
          );
        }
        return;
      }
      if (
        nextContinuationPromptAtRef.current != null
        && now >= nextContinuationPromptAtRef.current
      ) {
        setContinuationSeconds(CONTINUE_PROMPT_RESPONSE_MS / 1000);
        setContinuationPromptDeadline(now + CONTINUE_PROMPT_RESPONSE_MS);
      }
    };

    checkReadingLifetime();
    const timer = window.setInterval(checkReadingLifetime, 1000);
    return () => window.clearInterval(timer);
  }, [continuationPromptDeadline, phase]);

  const resetForRetry = () => {
    measurementActiveRef.current = false;
    releaseMicrophone();
    releasePlaybackAudio();
    setContinuationPromptDeadline(null);
    setPhase('preparation');
    setAttempt(null);
    setResult(null);
    setError('');
    setNotice('');
    startKeyRef.current = createIdempotencyKey('reading-start');
    completeKeyRef.current = createIdempotencyKey('reading-complete');
    startedAtMsRef.current = null;
    hiddenCountRef.current = 0;
    hiddenMsRef.current = 0;
    visibilityHiddenAtRef.current = null;
    maxScrollRatioRef.current = currentScrollRatio();
    nextContinuationPromptAtRef.current = null;
  };

  const handleStart = async () => {
    if (startInProgressRef.current || measurementActiveRef.current) return;
    startInProgressRef.current = true;
    const generation = ++startupGenerationRef.current;
    setPhase('starting');
    setError(current => current?.type === 'microphone-permission' ? current : '');
    setNotice('');
    try {
      await startAudioRecording(generation);
      if (!mountedRef.current || generation !== startupGenerationRef.current) {
        releaseMicrophone();
        if (mountedRef.current) setPhase('preparation');
        return;
      }
      const payload = isPreview
        ? {
          attempt_id: `preview-${Date.now()}`,
          attempt_type: 'official',
          status: 'started',
          started_at: new Date().toISOString(),
          passage,
        }
        : await startWeeklyReadingAttempt(
          round.student_assignment_id,
          boardSessionId,
          startKeyRef.current,
        );
      if (!isPreview) {
        cancellationContextRef.current = {
          assignmentId: round.student_assignment_id, attemptId: payload.attempt_id, sessionId: boardSessionId,
        };
      }
      if (!mountedRef.current || generation !== startupGenerationRef.current) {
        notifyCancellation('exit');
        releaseMicrophone();
        if (mountedRef.current) setPhase('preparation');
        return;
      }
      if (payload.status !== 'started') {
        cancellationContextRef.current = null;
        startKeyRef.current = createIdempotencyKey('reading-start');
        completeKeyRef.current = createIdempotencyKey('reading-complete');
        throw new Error('Bu deneme sona erdi. Okumaya yeniden başlayabilirsin.');
      }
      setAttempt(payload);
      setError('');
      // İstemci ile sunucu saatleri farklı olabilir; tarayıcı ölçümünü yerel
      // başlangıç anından, sunucu ölçümünü backend kendi saatinden yürütür.
      startedAtMsRef.current = Date.now();
      measurementActiveRef.current = true;
      nextContinuationPromptAtRef.current = Date.now() + CONTINUE_PROMPT_AFTER_MS;
      maxScrollRatioRef.current = currentScrollRatio();
      setPhase('reading');
    } catch (err) {
      measurementActiveRef.current = false;
      releaseMicrophone();
      if (!mountedRef.current) return;
      setError(current => err?.name === 'MicrophonePermissionError'
        ? { type: 'microphone-permission', failures: (current?.failures || 0) + 1 }
        : (err?.message || 'Okuma ölçümü başlatılamadı.'));
      setPhase('preparation');
    } finally {
      startInProgressRef.current = false;
    }
  };

  const handleComplete = async () => {
    if (!attempt || !measurementActiveRef.current) return;
    measurementActiveRef.current = false;
    setContinuationPromptDeadline(null);
    setPhase('submitting');
    setError('');
    if (visibilityHiddenAtRef.current != null) {
      hiddenMsRef.current += Math.max(0, Date.now() - visibilityHiddenAtRef.current);
      visibilityHiddenAtRef.current = Date.now();
    }
    const elapsedMs = Math.max(1_000, Date.now() - Number(startedAtMsRef.current || Date.now()));
    const measurement = {
      client_elapsed_ms: elapsedMs,
      visibility_hidden_count: hiddenCountRef.current,
      visibility_hidden_ms: hiddenMsRef.current,
      max_scroll_ratio: Math.max(maxScrollRatioRef.current, currentScrollRatio()),
    };
    try {
      const audioBlob = await stopAudioRecording();
      if (audioBlob?.size && window.URL?.createObjectURL) {
        releasePlaybackAudio();
        const playbackUrl = window.URL.createObjectURL(audioBlob);
        audioPlaybackUrlRef.current = playbackUrl;
        setAudioPlaybackUrl(playbackUrl);
      }
      const payload = isPreview
        ? {
          attempt_id: attempt.attempt_id,
          attempt_type: 'official',
          status: 'completed',
          elapsed_ms: elapsedMs,
          word_count: Number(visiblePassage.word_count || 0),
          words_per_minute: Math.round(
            Number(visiblePassage.word_count || 0) * 60_000 / elapsedMs,
          ),
          measurement_status: elapsedMs < 15000 ? 'invalid' : 'valid',
          invalid_reason: elapsedMs < 15000 ? 'too_short' : undefined,
          message: elapsedMs < 15000
            ? 'Çok kısa sürede tamamlandığı için bu ölçümü geçersiz saydık. İstersen yeniden deneyebilirsin.'
            : undefined,
          suspicious_reason_codes: [],
        }
        : await completeWeeklyReadingAttempt(
          round.student_assignment_id,
          attempt.attempt_id,
          boardSessionId,
          completeKeyRef.current,
          measurement,
        );
      cancellationContextRef.current = null;
      if (payload.measurement_status === 'invalid') {
        releasePlaybackAudio();
        setAttempt(null);
        setResult(null);
        setNotice(payload.message || 'Bu ölçümü geçersiz saydık. İstersen yeniden deneyebilirsin.');
        startKeyRef.current = createIdempotencyKey('reading-start');
        completeKeyRef.current = createIdempotencyKey('reading-complete');
        startedAtMsRef.current = null;
        nextContinuationPromptAtRef.current = null;
        setPhase('preparation');
        return;
      }
      const resultWithAnalysis = {
        ...payload,
        analysis: { status: audioBlob?.size ? 'uploading' : 'failed', failure_code: 'audio_empty' },
      };
      setResult(resultWithAnalysis);
      setPhase('result');
      onCompleted?.(resultWithAnalysis);
      if (audioBlob?.size) {
        try {
          const analysis = isPreview
            ? await analyzeWeeklyReadingPreviewAudio(
              previewToken,
              visiblePassage.id,
              boardSessionId,
              audioBlob,
              elapsedMs,
            )
            : await uploadWeeklyReadingAudio(
              round.student_assignment_id,
              attempt.attempt_id,
              boardSessionId,
              audioBlob,
              elapsedMs,
            );
          setResult((current) => ({ ...current, analysis }));
        } catch (uploadError) {
          setResult((current) => ({
            ...current,
            analysis: { status: 'failed', failure_code: 'upload_failed' },
          }));
          setError(uploadError?.message || (
            isPreview
              ? 'Preview ses analizi tamamlanamadı.'
              : 'Ses kaydı yüklenemedi; hız sonucunuz kaydedildi.'
          ));
        }
      }
    } catch (err) {
      notifyCancellation('exit');
      releaseMicrophone();
      setError(err?.message || 'Okuma sonucu kaydedilemedi.');
      setAttempt(null);
      startKeyRef.current = createIdempotencyKey('reading-start');
      completeKeyRef.current = createIdempotencyKey('reading-complete');
      startedAtMsRef.current = null;
      nextContinuationPromptAtRef.current = null;
      setPhase('preparation');
    }
  };

  const handleSkip = async () => {
    if (!onSkip || skipping || isBusy) return;
    setSkipping(true);
    setError('');
    try {
      const skipped = await onSkip();
      if (!skipped) setSkipping(false);
    } catch (err) {
      setError(err?.message || 'Haftalık Okuma ertelenemedi.');
      setSkipping(false);
    }
  };

  const handleContinueReading = () => {
    setContinuationPromptDeadline(null);
    setContinuationSeconds(CONTINUE_PROMPT_RESPONSE_MS / 1000);
    nextContinuationPromptAtRef.current = Date.now() + CONTINUE_PROMPT_REPEAT_MS;
  };

  const isBusy = phase === 'starting' || phase === 'submitting' || phase === 'cancelling';
  const analysis = result?.analysis || null;
  const analysisCompleted = analysis?.status === 'completed';
  const analysisFailed = analysis?.status === 'failed';
  const analysisPending = ['uploading', 'uploaded', 'processing'].includes(analysis?.status);
  const analysisTooLow = analysis?.failure_code === 'accuracy_too_low';
  const accuracy = analysisCompleted ? Number(analysis.accuracy_pct || 0) : null;
  const issueCards = analysisCompleted ? [
    { label: ALIGNMENT_STYLES.omission.label, value: analysis.omission_count || 0, symbol: '⏩', color: ALIGNMENT_STYLES.omission.color, bg: '#fff7ed' },
    { label: ALIGNMENT_STYLES.substitution.label, value: analysis.substitution_count || 0, symbol: '↔', color: ALIGNMENT_STYLES.substitution.color, bg: '#f5f3ff' },
    { label: ALIGNMENT_STYLES.insertion.label, value: analysis.insertion_count || 0, symbol: '+', color: ALIGNMENT_STYLES.insertion.color, bg: '#ecfdf5' },
    { label: ALIGNMENT_STYLES.repetition.label, value: analysis.repetition_count || 0, symbol: '↻', color: ALIGNMENT_STYLES.repetition.color, bg: '#fff1f2' },
  ] : [];

  if (phase === 'submitting' || (phase === 'result' && analysisPending)) {
    return <AnalysisWaitingScreen />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#fffaf0',
        color: '#172554',
        px: { xs: 1.5, sm: 3 },
        py: { xs: 1.5, sm: 3 },
        backgroundImage: 'radial-gradient(circle at 8% 10%, rgba(56,189,248,0.13), transparent 28%), radial-gradient(circle at 92% 90%, rgba(132,204,22,0.13), transparent 28%)',
      }}
    >
      <Box sx={{ maxWidth: 920, mx: 'auto' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => {
              if (measurementActiveRef.current) {
                cancelActiveReading('Okuma yarım kaldı. Yeniden başlayabilirsin.').then(() => onExit?.());
              } else {
                onExit?.();
              }
            }}
            disabled={isBusy}
            sx={{
              color: '#1d4ed8', fontWeight: 900, textTransform: 'none', minHeight: 44,
              '&.Mui-focusVisible': { outline: '2px solid #1d4ed8', outlineOffset: 2 },
            }}
          >
            Mini Görevlere Dön
          </Button>
          {isPreview && (
            <Chip
              label="TEST MODU"
              sx={{ bgcolor: '#facc15', color: '#713f12', fontWeight: 1000 }}
            />
          )}
        </Stack>

        <Paper
          elevation={0}
          sx={{
            border: '1px solid #dbeafe',
            borderRadius: { xs: 2.5, sm: 4 },
            overflow: 'hidden',
            boxShadow: '0 18px 46px rgba(15,23,42,0.09)',
          }}
        >
          <Box sx={{ bgcolor: '#5128bc', color: '#fff', px: { xs: 2, sm: 4 }, py: { xs: 2.2, sm: 3 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                  <MenuBookRoundedIcon />
                  <Typography sx={{ fontWeight: 900 }}>Okuma Çalışması</Typography>
                </Stack>
                <Typography variant="h3" sx={{ fontWeight: 1000, fontSize: { xs: 30, sm: 42 }, lineHeight: 1.1 }}>
                  {passage.title || 'Haftanın Okuması'}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, alignSelf: { sm: 'flex-end' } }}>
                <Typography sx={{ fontWeight: 800 }}>{classTitle || 'Mini Görevler'}</Typography>
                <Typography sx={{ opacity: 0.85, fontSize: 14 }}>
                  {passage.word_count ? `${passage.word_count} kelime` : ''}
                  {passage.sequence_position ? ` • ${passage.sequence_position}. metin` : ''}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box sx={{ px: { xs: 2.2, sm: 5 }, py: { xs: 3, sm: 5 }, bgcolor: '#fff' }}>
            {error?.type === 'microphone-permission' ? (
              <MicrophonePermissionHelp onRetry={handleStart} busy={phase === 'starting'} failures={error.failures} />
            ) : error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {notice ? <Alert severity="warning" sx={{ mb: 2 }}>{notice}</Alert> : null}

            {phase === 'cancelling' ? (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
                <CircularProgress />
                <Typography sx={{ color: '#475569', fontWeight: 800 }}>Kayıt güvenle durduruluyor…</Typography>
              </Stack>
            ) : null}

            {(phase === 'preparation' || phase === 'starting') && (
              <Stack spacing={2.2} alignItems="flex-start">
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <TimerRoundedIcon sx={{ color: '#7c3aed', fontSize: 34 }} />
                  <Typography variant="h5" sx={{ fontWeight: 900 }}>Okumaya hazır mısın?</Typography>
                </Stack>
                <Typography sx={{ color: '#475569', fontSize: 18, lineHeight: 1.65 }}>
                  Başla düğmesine dokun, metni sesli oku. Bitirdiğinde “Okumayı tamamladım” düğmesine dokun.
                </Typography>
                <Alert severity="info" icon={<MicRoundedIcon fontSize="inherit" />}>
                  <strong>Okuma değerlendirmesi hakkında:</strong> Bu çalışma sırasında öğrencinin sesi
                  geçici olarak kaydedilir. Ses kaydı ve öğrencinin okuduğu metin, konuşmayı metne
                  dönüştürmek ve okuma performansını değerlendirmek amacıyla yurt dışında bulunan OpenAI
                  hizmetine gönderilir. Oluşturulan ham transkript değerlendirme tamamlandıktan sonra
                  silinir. Analiz için yüklenen tüm ses kayıtları, değerlendirme durumundan bağımsız olarak
                  bir sonraki gece Türkiye saatiyle 03.00'e kadar tutulur ve o saatte otomatik olarak
                  silinir. Renkli kelime karşılaştırması ekranda
                  gösterilebilmesi için geçici olarak tutulur ve en fazla 25 saat içinde silinir. Tarayıcı
                  sorduğunda mikrofon için “İzin ver” seçeneğine dokun.
                  {' '}<a href="/legal/privacy.html" target="_blank" rel="noreferrer">
                    Ayrıntılı aydınlatmayı görüntüle
                  </a>.
                </Alert>
                {resumedAttempt ? (
                  <Alert severity="warning">
                    Yarım kalan bir ölçüm bulundu. Başladığında süre ve ses kaydı birlikte yeniden başlatılacak.
                  </Alert>
                ) : null}
                {error?.type !== 'microphone-permission' ? <Button
                  variant="contained"
                  size="large"
                  startIcon={phase === 'starting' ? <CircularProgress size={20} color="inherit" /> : <PlayArrowRoundedIcon />}
                  disabled={phase === 'starting'}
                  onClick={handleStart}
                  sx={{ borderRadius: 2.5, px: 3, py: 1.3, fontWeight: 900, textTransform: 'none' }}
                >
                  Bilgilendirmeyi gördüm, okumaya başla
                </Button> : null}
                {onSkip ? (
                  <Button
                    variant="text"
                    disabled={skipping || phase === 'starting'}
                    onClick={handleSkip}
                    sx={{ color: '#475569', fontWeight: 800, textTransform: 'none' }}
                  >
                    {skipping ? 'Diğer etkinlikler açılıyor…' : 'Şimdilik atla, diğer etkinlikleri yap'}
                  </Button>
                ) : null}
              </Stack>
            )}

            {(phase === 'reading' || phase === 'submitting') && (
              <>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <MicRoundedIcon sx={{ color: '#ef4444' }} />
                  <Typography sx={{ color: '#475569', fontWeight: 800 }}>
                    Süre ölçülüyor ve ses kaydediliyor. Metni sesli ve doğal bir hızda oku.
                  </Typography>
                </Stack>
                <Typography
                  component="div"
                  sx={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontSize: { xs: 22, sm: 27 },
                    lineHeight: { xs: 1.72, sm: 1.85 },
                    letterSpacing: '0.01em',
                    color: '#1e293b',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {visiblePassage.text || 'Okuma metni bulunamadı.'}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  color="success"
                  startIcon={phase === 'submitting' ? <CircularProgress size={20} color="inherit" /> : <StopCircleRoundedIcon />}
                  disabled={phase === 'submitting'}
                  onClick={handleComplete}
                  sx={{ mt: 4, borderRadius: 2.5, py: 1.4, fontWeight: 900, textTransform: 'none', fontSize: 17 }}
                >
                  Okumayı tamamladım
                </Button>
              </>
            )}

            {phase === 'result' && result && (
              <Stack spacing={2.4} alignItems="stretch">
                <Box
                  sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 3.5,
                    px: { xs: 2.2, sm: 3 },
                    py: { xs: 2.4, sm: 2.8 },
                    background: analysisTooLow
                      ? 'linear-gradient(125deg, #fff7ed 0%, #fffbeb 55%, #eff6ff 100%)'
                      : 'linear-gradient(125deg, #ecfdf5 0%, #f0fdfa 55%, #eff6ff 100%)',
                    border: `1px solid ${analysisTooLow ? '#fed7aa' : '#bbf7d0'}`,
                    '&::before': {
                      content: '"✦  ·  ✦"',
                      position: 'absolute',
                      right: 22,
                      top: 10,
                      color: '#f59e0b',
                      fontSize: 28,
                      transform: 'rotate(-8deg)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: { xs: 64, sm: 82 },
                        height: { xs: 64, sm: 82 },
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        flex: '0 0 auto',
                        background: analysisTooLow
                          ? 'linear-gradient(145deg, #f59e0b, #ea580c)'
                          : 'linear-gradient(145deg, #22c55e, #059669)',
                        color: '#fff',
                        boxShadow: '0 12px 26px rgba(5,150,105,0.28)',
                      }}
                    >
                      {analysisTooLow
                        ? <MicRoundedIcon sx={{ fontSize: { xs: 42, sm: 54 } }} />
                        : <CheckCircleRoundedIcon sx={{ fontSize: { xs: 42, sm: 54 } }} />}
                    </Box>
                    <Box>
                      <Typography sx={{ color: analysisTooLow ? '#c2410c' : '#047857', fontWeight: 1000, fontSize: { xs: 30, sm: 42 }, lineHeight: 1 }}>
                        {analysisTooLow ? 'Bir kez daha deneyelim' : 'Harika!'}
                      </Typography>
                      <Typography sx={{ color: '#172554', fontWeight: 950, fontSize: { xs: 22, sm: 30 }, lineHeight: 1.15 }}>
                        {analysisTooLow ? 'Sesini yeterince net algılayamadık' : 'Okuman tamamlandı!'}
                      </Typography>
                      <Typography sx={{ mt: 0.6, color: '#64748b', fontWeight: 700 }}>
                        {analysisTooLow ? 'Bu denemeyi sonuçlarına eklemedik.' : 'Emek verdiğin için tebrik ederiz. 🎉'}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {!analysisTooLow ? <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1.5,
                  }}
                >
                  <ResultMetricCard
                    icon={<SpeedRoundedIcon />}
                    label="Okuma hızı"
                    color="#047857"
                    background="linear-gradient(145deg, #ecfdf5, #f0fdfa)"
                    helper="Ritmini koru! 🚀"
                  >
                    <Typography sx={{ color: '#047857', fontSize: { xs: 42, sm: 48 }, fontWeight: 1000, lineHeight: 1 }}>
                      {result.words_per_minute}
                    </Typography>
                    <Typography sx={{ color: '#065f46', fontSize: 18, fontWeight: 950 }}>kelime/dk</Typography>
                  </ResultMetricCard>
                  <ResultMetricCard
                    icon={<AccessTimeRoundedIcon />}
                    label="Okuma süresi"
                    color="#1d4ed8"
                    background="linear-gradient(145deg, #eff6ff, #eef2ff)"
                    helper="Ölçüm tamamlandı ⏱️"
                  >
                    <Typography sx={{ color: '#1d4ed8', fontSize: { xs: 32, sm: 38 }, fontWeight: 1000, lineHeight: 1.2 }}>
                      {formatDuration(result.elapsed_ms)}
                    </Typography>
                  </ResultMetricCard>
                  <ResultMetricCard
                    icon={<AutoAwesomeRoundedIcon />}
                    label="Tahmini doğru okuma"
                    color="#6d28d9"
                    background="linear-gradient(145deg, #faf5ff, #f5f3ff)"
                    helper={analysisCompleted
                      ? accuracyMessage(accuracy)
                      : (analysisFailed ? 'Analiz tamamlanamadı.' : 'Sesin analiz ediliyor…')}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <CircularProgress
                        variant={analysisPending ? 'indeterminate' : 'determinate'}
                        value={analysisCompleted ? accuracy : 0}
                        size={88}
                        thickness={5}
                        sx={{ color: analysisCompleted ? '#22c55e' : '#8b5cf6' }}
                      />
                      <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                        <Typography sx={{ color: '#312e81', fontSize: 21, fontWeight: 1000 }}>
                          {analysisCompleted ? `%${accuracy}` : (analysisPending ? '…' : '!')}
                        </Typography>
                      </Box>
                    </Box>
                  </ResultMetricCard>
                </Box> : null}

                {analysis?.status === 'failed' ? (
                  <Alert severity="warning">
                    {analysisTooLow
                      ? 'Okumanı yeterince net algılayamadık. Daha sessiz bir ortamda, mikrofona yakın ve anlaşılır şekilde okuyarak tekrar deneyebilirsin.'
                      : 'Ses analizi tamamlanamadı; süre ve hız sonucun hazır.'}
                  </Alert>
                ) : null}

                {analysisCompleted ? (
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      borderRadius: 3.5,
                      background: 'linear-gradient(135deg, #fffaf0, #fff7ed)',
                      border: '1px solid #fed7aa',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.3 }}>
                      <GraphicEqRoundedIcon sx={{ color: '#f59e0b' }} />
                      <Typography sx={{ fontWeight: 950, fontSize: 19 }}>Birlikte geliştirebileceğimiz yerler</Typography>
                    </Stack>
                    <Typography sx={{ color: '#64748b', mb: 1.8 }}>
                      Bunlar hata notu değil; bir sonraki okumada dikkat edebileceğin küçük ipuçları.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.2 }}>
                      {issueCards.map((item) => (
                        <Box key={item.label} sx={{ p: 1.5, bgcolor: item.bg, borderRadius: 2.5, border: '1px solid rgba(148,163,184,0.18)' }}>
                          <Stack direction="row" spacing={0.8} alignItems="center">
                            <Typography sx={{ color: item.color, fontWeight: 1000, fontSize: 24 }}>{item.symbol}</Typography>
                            <Typography sx={{ color: '#334155', fontWeight: 800, fontSize: 13 }}>{item.label}</Typography>
                          </Stack>
                          <Typography sx={{ color: '#172554', fontWeight: 1000, fontSize: 30, lineHeight: 1.1 }}>{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {(analysisCompleted || analysisTooLow) && audioPlaybackUrl ? (
                  <TransientAudioPlayer src={audioPlaybackUrl} fallbackDurationMs={result.elapsed_ms} />
                ) : null}

                {analysisCompleted ? <ReadingAlignment rows={analysis.alignment} /> : null}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  {isPreview || analysisTooLow ? (
                    <Button
                      variant="outlined"
                      startIcon={<ReplayRoundedIcon />}
                      onClick={resetForRetry}
                      size="large"
                      sx={{ flex: 1, py: 1.2, borderRadius: 2.5, fontWeight: 900, textTransform: 'none' }}
                    >
                      {analysisTooLow ? 'Tekrar Dene' : 'Tekrar oku'}
                    </Button>
                  ) : null}
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<AutoAwesomeRoundedIcon />}
                    onClick={onExit}
                    sx={{
                      flex: 1,
                      py: 1.2,
                      borderRadius: 2.5,
                      fontWeight: 950,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                    }}
                  >
                    Tamam
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>

          <Box sx={{ px: { xs: 2.2, sm: 4 }, py: 1.6, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
              {passage.code || 'Okuma metni'}
              {passage.genre ? ` • ${passage.genre}` : ''}
            </Typography>
          </Box>
        </Paper>

        <Dialog
          open={phase === 'reading' && continuationPromptDeadline != null}
          aria-labelledby="weekly-reading-continue-title"
          PaperProps={{ sx: { width: 'min(92vw, 480px)', borderRadius: 4, p: 0.5 } }}
        >
          <DialogTitle id="weekly-reading-continue-title" sx={{ fontWeight: 1000, color: '#312e81' }}>
            Okumaya devam ediyor musun? 📖
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#475569', lineHeight: 1.65 }}>
              Kayıt bir süredir açık. Okuyorsan devam edebilirsin; bitirdiysen sonucunu hemen alabilirsin.
            </Typography>
            <Box
              sx={{
                mt: 2,
                mx: 'auto',
                width: 72,
                height: 72,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: '#fff',
                background: 'linear-gradient(145deg, #8b5cf6, #4f46e5)',
                boxShadow: '0 12px 28px rgba(79,70,229,0.25)',
              }}
            >
              <Typography sx={{ fontSize: 27, fontWeight: 1000 }}>{continuationSeconds}</Typography>
            </Box>
            <Typography sx={{ mt: 1, color: '#64748b', textAlign: 'center', fontSize: 13 }}>
              Yanıt verilmezse kayıt silinerek ölçüm yeniden başlatılır.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleComplete}
              sx={{ borderRadius: 2.5, fontWeight: 900, textTransform: 'none' }}
            >
              Okumayı tamamladım
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleContinueReading}
              autoFocus
              sx={{ borderRadius: 2.5, fontWeight: 900, textTransform: 'none' }}
            >
              Devam ediyorum
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
